import { getReportLenses, getStandardReportLens } from '@opengov/api-deprecated';
import { getEntityFiscalYearStartMonth, getEntitySubdomain, selectCurrentEntity } from '@opengov/app-core/entity';
import { composeEffects } from '@opengov/component-library-classic/module/module';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import { selectCurrentLocale } from '@opengov/locale/locales';
import { EOgqlJobState, IOgqlV1Column, IOgqlV1DataAndColumns } from '@opengov/ogql-types';
import { accountSummaryFormatters } from '@opengov/reporting-formatters/formatters/accountSummaryFormatters';
import {
  calendarUnitFormatters,
  getCalendarUnit
} from '@opengov/reporting-formatters/formatters/calendarUnitFormatters';
import {
  columnsSelectionToSortType,
  mapQueryRowDataToColumnLabel,
  mergeColumnsFromMultipleQueries,
  ogqlColumnsToAgGrid,
  ogqlDataToAgGrid,
  removeEmptyQueryResults
} from '@opengov/reporting-transforms/summaryTable';
import {
  CalendarUnit,
  ColumnValueSegment,
  EQueryType,
  IColumnSource,
  IReportConfiguration,
  IReportDatasetLens,
  getLensIdsFromReport,
  isMultiHierarchyCalculatedColumnGroup,
  IAccountSummary,
  MultiHierarchyAccountSummary
} from '@opengov/reporting-types';
import { IDimensionalDateReportColumn } from '@opengov/reporting-types/src/reportColumns-immer';
import { produce } from 'immer';
import { getAccessToken } from '@opengov/app-core/auth/accessTokenForNonReactContextsStore';
import * as api from '../../../src/api/reportingApi';
import {
  findNewlyBrokenCalcColumns,
  type BrokenCalcColumn
} from '../../../src/components/editor/sidepanel/Calculations/findBrokenCalcColumns';
import { buildDeletedColumnGroupPlaceholder, getColumnData } from '../../../src/components/table/getColumnData';
import {
  takeOverrideRowPropagation,
  updateMultiHierarchyColumnGroup
} from '../../common/reportConfiguration/currentReportActions';
import {
  selectNextGenGridCacheFlag,
  selectOrganizeColumnsByAccountTypeFlag,
  selectReportingPerformancePocFlag
} from '../../common/flags';
import { showPermissionErrorToast } from '../../common/helpers/permissionErrorHelper';
import { MuiToastTypes } from '../../common/helpers/utils';
import { ILocaleFormatter, IReportLocale, reportLocaleText } from '../../common/locale';
import { selectCurrentReportConfigurationId } from '../../common/report/selectors/reportSelectors';
import { reportActionCreators } from '../../common/report/state';
import { runV1Query } from '../../common/reportConfiguration/effects/queries/runQueries';
import { selectCurrentReportConfiguration, selectIsInStandardReport } from '../../common/reportConfiguration/selectors';
import { getValidColumnGroups } from '../../common/reportConfiguration/utils';
import { lensEffects } from '../../lenses/lensEffects';
import { IReportDatasetLensesStatus, lensActionCreators } from '../../lenses/lensState';
import { selectDimensionalAccountTypes, selectReportCoaJoinColName } from '../selectors/selectReportDimension';
import { selectInEditMode, selectPivotColumns } from '../selectors/selectReportExplorerState';
import { onMultiDatasetColumnClick } from '../columnClickHandler';
import { reportExplorerActionCreators } from '../state';
import { DataTableStatus } from '../types';
import { isStillFreshOpen, takeReusableColumns } from '../loadedColumnsMemo';

export interface IDimensionalAgGridRowItem {
  name: string[];
  queryColumns: string[];
  isGroup: boolean;
  isRowEmpty: boolean;
  rowId: string;

  [columnName: string]: any;
}

export enum EQueryColumnSortType {
  DIMENSIONAL = 'dimensional',
  YEAR_DATE = 'yearDate',
  FULL_DATE = 'fullDate'
}

const isNotString = (input: string | IReportDatasetLens): input is IReportDatasetLens => {
  return typeof input !== 'string';
};

export const headerValueGetter = (header: string, columnSource: IColumnSource, lens?: IReportDatasetLens) => {
  return () => {
    const calendarUnit = getCalendarUnit(columnSource, lens?.columns);

    if (calendarUnit) {
      // Fiscal quarter is already formatted in backend for MDS reports
      if (calendarUnit !== CalendarUnit.FiscalQuarter) {
        return calendarUnitFormatters[calendarUnit].formatter()(header);
      }
    }
    return header;
  };
};

/**
 * Every lens the report reads from, as an array.
 *
 * Delegates to `getLensIdsFromReport` rather than repeating the walk. Two local collectors used to
 * live here — one per account-summary variant — and they drifted: neither included ERP override rows'
 * column groups, so a lens referenced only by an override row was never prefetched. The shared helper
 * already handles both variants, both flavours of override row, and skips calculated column groups,
 * which have no lens (BUD-18427).
 */
export const getReportLensIds = (accountSummary: IAccountSummary | MultiHierarchyAccountSummary): string[] =>
  Array.from(getLensIdsFromReport(accountSummary));

export const getColumnGroupLenses = async (
  reportConfiguration: IReportConfiguration
): Promise<(string | IReportDatasetLens)[]> => {
  const { accountSummary } = reportConfiguration;
  if (!accountSummary) return [];
  const lensIds = getReportLensIds(accountSummary);
  return Promise.all(lensIds.map((lensId) => lensEffects.getLensWithColumns(lensId)));
};

export const ensureDimensionalRowsColumnJoinColumnName = (
  reportConfiguration: IReportConfiguration,
  joinColumnName: string
): IReportConfiguration =>
  produce(reportConfiguration, (newReportConfiguration) => {
    newReportConfiguration.dimensionalSummaryQueries = newReportConfiguration.dimensionalSummaryQueries.map((query) => {
      query.dimension.rowsColumn.joinColumnName = joinColumnName;
      return query;
    });
  });

type UpdateMultiHierarchyColumnGroupPayload = Parameters<typeof updateMultiHierarchyColumnGroup>[1];

/** The column editor's update payload; it leaves `aggregation` unset, as its Apply always has. */
type ColumnGroupUpdatePreviewPayload = Omit<UpdateMultiHierarchyColumnGroupPayload, 'aggregation'> & {
  aggregation?: UpdateMultiHierarchyColumnGroupPayload['aggregation'];
};

export interface IGridColumnsForQueryArgs {
  reportConfiguration: IReportConfiguration;
  reportConfigurationId: string;
  formatter: ILocaleFormatter;
  reportLocaleText: IReportLocale;
  gridCacheEnabled: boolean;
  entitySubdomain: string;
  entityFiscalYearStartMonth: number;
  isEditing: boolean;
  /** The `reporting-performance-poc` flag. */
  fastInitialLoad?: boolean;
}

/**
 * Builds the grid's columns for the current report configuration and asks the grid to refresh its
 * rows. This is the column step of the query effect.
 *
 * With `fastInitialLoad`, columns the route loader built moments ago for exactly this configuration
 * are reused instead of being built again (a second build forces ag-grid to lay the table out
 * again), and a run that belongs to the loader's open keeps the pivot cache, which at that point
 * holds the rows the prefetch just fetched. Every other run, and every run without it, refreshes as
 * before.
 */
export async function loadGridColumnsForQuery({
  reportConfiguration,
  reportConfigurationId,
  formatter,
  gridCacheEnabled,
  reportLocaleText,
  entitySubdomain,
  entityFiscalYearStartMonth,
  isEditing,
  fastInitialLoad = false
}: IGridColumnsForQueryArgs): Promise<void> {
  reportExplorerActionCreators.updateDataTableStatus(DataTableStatus.Loading);
  try {
    /** Excludes column groups that were retained but reference a deleted COA group */
    const validColumnGroups = getValidColumnGroups(reportConfiguration);
    if (validColumnGroups.length === 0) {
      reportExplorerActionCreators.setagGridColumns(
        buildDeletedColumnGroupPlaceholder({
          columnGroups: reportConfiguration.accountSummary?.columnGroups ?? [],
          onColumnClick: (columnIndex) => reportExplorerActionCreators.setEditColEditMode(columnIndex)
        })
      );
      reportExplorerActionCreators.updateDataTableStatus(DataTableStatus.Current);
      return;
    }

    const accountSummary = reportConfiguration.accountSummary;
    const report = { ...accountSummary, columnGroups: validColumnGroups } as NonNullable<typeof accountSummary>;
    const reusedColumns = fastInitialLoad
      ? takeReusableColumns({ reportConfigurationId, accountSummary, isEditing })
      : null;
    const { agGridColumns, columnValues } =
      reusedColumns ??
      (await getColumnData(
        {
          localeText: reportLocaleText['multidataset'],
          formatter,
          gridCacheEnabled,
          entitySubdomain,
          entityFiscalYearStartMonth,
          isEditing
        },
        report,
        reportConfigurationId,
        { onColumnClick: onMultiDatasetColumnClick }
      ));

    reportExplorerActionCreators.setagGridColumns({
      agGridColumns,
      pivotColumns: columnValues,
      pivotColumnGroupIds: validColumnGroups.map((group) => group.id)
    });

    // The pivot cache holds fresh rows only for the open the loader just made. The runs that belong to
    // it keep the cache; any other run (a saved-view change, a filter, an edit, a later refresh) wipes it.
    const keepsTheOpensCache = fastInitialLoad && isStillFreshOpen({ reportConfigurationId, accountSummary });
    if (keepsTheOpensCache) {
      reportExplorerActionCreators.requestRowRefreshKeepingCache();
    } else {
      reportExplorerActionCreators.setIsRefreshRowData(true);
    }
  } catch (error) {
    reportExplorerActionCreators.updateDataTableStatus(DataTableStatus.Error);
    throw error;
  }
}

export const reportDimensionalEffects = composeEffects('report-dimensional', (compose) => ({
  getReportDatasetLenses: compose.effect(async ({ reportId }) => {
    return getReportLenses(reportId)
      .then((res) => {
        // no data no report possible
        const filtered = res.filter((lens) => !!lens.dataUpdatedAt);
        reportExplorerActionCreators.setReportDatasetLenses(filtered);
      })
      .catch(showPermissionErrorToast);
  }),
  getReportDatasetLensesStatus: compose.effect(async ({ reportId }) => {
    return getReportLenses(reportId)
      .then((res) => {
        const filtered = res.map((lens) => {
          const { dataUpdatedAt, id, name, ingestionStatus } = lens;
          if (dataUpdatedAt) {
            return { dataUpdatedAt, id, name, ingestionStatus };
          }
        });

        lensActionCreators.setReportDatasetLensesStatus(filtered as IReportDatasetLensesStatus[]);
      })
      .catch(showPermissionErrorToast);
  }),
  getMultiDatasetReportLenses: compose.statefulEffect(
    selectCurrentEntity,
    async (currentEntity, reportConfiguration: IReportConfiguration) => {
      reportExplorerActionCreators.setIsLoadingReportDatasetLenses(true);
      try {
        const lenses = await getColumnGroupLenses(reportConfiguration);
        const validLenses = lenses.filter(isNotString);
        reportExplorerActionCreators.setReportDatasetLenses(validLenses);
      } finally {
        // Always reset the loading flag — including when a single lens fetch
        // throws and Promise.all rejects — so the UI doesn't stay stuck loading.
        reportExplorerActionCreators.setIsLoadingReportDatasetLenses(false);
      }
    }
  ),
  getLens: compose.effect(async ({ lensId, entityUuid }) => getStandardReportLens(lensId, entityUuid)),

  getColumnData: compose.statefulPayloadlessEffect(
    createStructuredSelector({
      reportConfiguration: selectCurrentReportConfiguration,
      reportConfigurationId: selectCurrentReportConfigurationId,
      formatter: createSelector(
        selectCurrentReportConfiguration,
        selectCurrentLocale,
        (currentReportConfiguration, currentLocale) => {
          // Pass formatting options and current locale
          return accountSummaryFormatters(currentReportConfiguration.accountSummary?.formatting, currentLocale.locale);
        }
      ),
      gridCacheEnabled: selectNextGenGridCacheFlag,
      reportLocaleText,
      entitySubdomain: getEntitySubdomain,
      entityFiscalYearStartMonth: getEntityFiscalYearStartMonth,
      organizeColumnsByAccountType: selectOrganizeColumnsByAccountTypeFlag,
      isEditing: selectInEditMode,
      fastInitialLoad: selectReportingPerformancePocFlag
    }),
    loadGridColumnsForQuery
  ),

  /**
   * The visible calc columns a pending column-group edit would newly blank, so the editor can warn
   * before applying it.
   *
   * Which columns a group yields depends on its data — the months with posted rows, the values its
   * filters keep — so the answer needs the draft's real column values, not just its config. The
   * draft is built by the same reducer Apply dispatches, so the preview cannot disagree with it.
   * Reports without calc columns skip the request entirely.
   */
  previewNewlyBrokenCalcColumns: compose.statefulEffect(
    createStructuredSelector({
      reportConfiguration: selectCurrentReportConfiguration,
      reportConfigurationId: selectCurrentReportConfigurationId,
      pivotColumns: selectPivotColumns,
      gridCacheEnabled: selectNextGenGridCacheFlag,
      entitySubdomain: getEntitySubdomain,
      entityFiscalYearStartMonth: getEntityFiscalYearStartMonth
    }),
    async (
      {
        reportConfiguration,
        reportConfigurationId,
        pivotColumns,
        gridCacheEnabled,
        entitySubdomain,
        entityFiscalYearStartMonth
      }: {
        reportConfiguration: IReportConfiguration;
        reportConfigurationId: string;
        pivotColumns: ColumnValueSegment[][];
        gridCacheEnabled: boolean;
        entitySubdomain: string;
        entityFiscalYearStartMonth: number;
      },
      payload: ColumnGroupUpdatePreviewPayload
    ): Promise<BrokenCalcColumn[]> => {
      const currentColumnGroups = reportConfiguration.accountSummary?.columnGroups ?? [];
      // Without the current columns there is no "before" to compare against, and every calc column
      // that is already blank would be reported as newly blanked by this edit.
      const hasCurrentColumns = pivotColumns.length > 0;
      if (!hasCurrentColumns || !currentColumnGroups.some(isMultiHierarchyCalculatedColumnGroup)) {
        return [];
      }

      const draftConfiguration = produce(reportConfiguration, (draft) => {
        updateMultiHierarchyColumnGroup(draft, payload as UpdateMultiHierarchyColumnGroupPayload);
      });
      // The reducer records an override-row summary for the toast; a preview must not leave one behind.
      takeOverrideRowPropagation();

      const draftColumnGroups = getValidColumnGroups(draftConfiguration);
      const draftReport = { ...draftConfiguration.accountSummary, columnGroups: draftColumnGroups } as NonNullable<
        IReportConfiguration['accountSummary']
      >;
      const { data: draftPivotColumns } = await api.pivotCols(
        { report: draftReport, reportConfigurationId, entitySubdomain, entityFiscalYearStartMonth },
        { cache: gridCacheEnabled, accessToken: await getAccessToken() }
      );

      return findNewlyBrokenCalcColumns({
        before: { columnGroups: currentColumnGroups, pivotColumns },
        after: { columnGroups: draftColumnGroups, pivotColumns: draftPivotColumns }
      });
    }
  ),

  getDimensionalGroupData: compose.statefulEffect(
    createStructuredSelector({
      accountTypes: selectDimensionalAccountTypes,
      text: reportLocaleText,
      isStandardTemplate: selectIsInStandardReport,
      joinColumnName: selectReportCoaJoinColName
    }),
    async ({ accountTypes, text, isStandardTemplate, joinColumnName }, reportConfiguration: IReportConfiguration) => {
      reportExplorerActionCreators.setDimensionalQueriesLoading(reportConfiguration.dimensionalSummaryQueries);
      // this Effect is to get the row of total group data
      // this has specific needs for the ag-grid serverSideDataSource
      // that are diff than getting the children
      const summaryQueryDateColumn: string = (
        reportConfiguration.dimensionalSummaryQueries[0].dimension.columnsColumn as IDimensionalDateReportColumn
      ).dateColumnName;
      try {
        const queryResults: IOgqlV1DataAndColumns[] = await Promise.all(
          reportConfiguration.dimensionalSummaryQueries.map(async (_, dimensionalSummaryQueryIndex) => {
            const v1QueryParams = {
              queryType: EQueryType.dimensionalSummaryTableGroupData,
              useLargeScaleQueryLimit: false,
              reportConfiguration: ensureDimensionalRowsColumnJoinColumnName(reportConfiguration, joinColumnName),
              useQueryService: true,
              isStandardTemplate,
              dimensionalSummaryQueryIndex
            };

            const extractDataAndColumnsFromQueryResult = (result) => ({
              data: result.jobResult.data,
              columns: result.jobResult.metadata.columns
            });

            return runV1Query(v1QueryParams)
              .then((result) => {
                // eslint-disable-next-line max-len
                const willIssueChildQuery =
                  reportConfiguration.presentationConfiguration.dimensionalSummaryBlockExpandedStates.find(
                    (expandedState) => expandedState.controlBlockIndex === dimensionalSummaryQueryIndex
                  );
                if (!willIssueChildQuery) {
                  reportExplorerActionCreators.setDimensionalQueryFinished(dimensionalSummaryQueryIndex);
                }
                return extractDataAndColumnsFromQueryResult(result);
              })
              .catch(() => {
                reportExplorerActionCreators.setDimensionalQueryFinished(dimensionalSummaryQueryIndex);
                // if the query fails continue processing the results
                return { data: [[]], columns: [] };
              });
          })
        );

        const queryResultsWithDataAndColumns = removeEmptyQueryResults(queryResults);

        const mergedColumnsFromQueryResults = mergeColumnsFromMultipleQueries(
          queryResultsWithDataAndColumns,
          columnsSelectionToSortType(summaryQueryDateColumn)
        );

        const columnLabelRowDataMap: {
          [columnLabel: string]: any;
        }[] = queryResults
          .map((qr) => {
            const rowColumnsWithoutGroupColumns = qr.columns.slice(1);
            return mapQueryRowDataToColumnLabel(qr.data, rowColumnsWithoutGroupColumns, false);
          })
          .map((d) => d[0]);

        const ledgerTypeLabels: IOgqlV1Column['label'][] = queryResults.map((_, index) => {
          const match = accountTypes.find(
            (a) => reportConfiguration.dimensionalSummaryQueries[index].dimension.showAccountType === a.filterValue
          );
          return match.label;
        });

        const { dimensionalSummaryBlockNames } = reportConfiguration.presentationConfiguration;
        if (dimensionalSummaryBlockNames) {
          dimensionalSummaryBlockNames.forEach((controlBlockName) => {
            const { controlBlockIndex, displayName } = controlBlockName;
            ledgerTypeLabels[controlBlockIndex] = displayName;
          });
        }
        const cols = ogqlColumnsToAgGrid(mergedColumnsFromQueryResults);
        const dataFormattedForAgGrid = ogqlDataToAgGrid(columnLabelRowDataMap, cols, ledgerTypeLabels, true);

        dataFormattedForAgGrid.forEach((row, queryIndex) => {
          if (row.isRowEmpty) {
            reportExplorerActionCreators.setDimensionalQueryFinished(queryIndex);
          }
        });

        reportExplorerActionCreators.setDimensionalData({
          data: dataFormattedForAgGrid,
          columns: cols,
          reportConfiguration: reportConfiguration
        });
        reportExplorerActionCreators.updateDataTableStatus(DataTableStatus.Current);
      } catch (error) {
        console.error(error);
        reportActionCreators.toggleMuiToast({
          type: MuiToastTypes.FAILURE,
          headerText: text.query.failure.headerText,
          bodyText: text.query.failure.bodyText,
          dataTest: `toast-dimensional-group-query-failure`
        });

        reportExplorerActionCreators.setDimensionalData({
          data: [],
          columns: [],
          reportConfiguration: reportConfiguration
        });
        reportExplorerActionCreators.updateDataTableStatus(DataTableStatus.Error);
      }
    }
  ),
  getDimensionalChildData: compose.statefulEffect(
    createStructuredSelector({
      text: reportLocaleText,
      joinColumnName: selectReportCoaJoinColName,
      isStandardTemplate: selectIsInStandardReport
    }),
    async (
      { text, joinColumnName, isStandardTemplate },
      e: {
        reportConfiguration: IReportConfiguration;
        limit: number;
        offset: number;
        index: number;
      }
    ) => {
      // this Effect is to get the children of a group
      // it is called from the ag-grid serverSideDataSource
      // the results are not stored in our state
      const dimensionalSummaryQuery = e.reportConfiguration.dimensionalSummaryQueries[e.index];
      const showCodeAndDescription = !!dimensionalSummaryQuery.dimension.rowsColumn.accountCodeColumnRef;

      // we might need to patch the reportConfiguration because old ones might not have the joinColumnName
      let patchedReportConfiguration = e.reportConfiguration;

      if (e.reportConfiguration.dimensionalSummaryQueries.find((q) => !q.dimension.rowsColumn.joinColumnName)) {
        patchedReportConfiguration = produce(patchedReportConfiguration, (newState) => {
          newState.dimensionalSummaryQueries = e.reportConfiguration.dimensionalSummaryQueries.map((query) => {
            query.dimension.rowsColumn.joinColumnName = joinColumnName;
            return query;
          });
        });
      }

      return runV1Query({
        reportConfiguration: patchedReportConfiguration,
        queryType: EQueryType.dimensionalSummaryTableChildData,
        useLargeScaleQueryLimit: false,
        useQueryService: true,
        limit: e.limit,
        isStandardTemplate,
        offset: e.offset,
        dimensionalSummaryQueryIndex: e.index
      }).then((result) => {
        reportExplorerActionCreators.setDimensionalQueryFinished(e.index);
        switch (result.state) {
          case EOgqlJobState.finished: {
            const columns = result.jobResult.metadata.columns;
            const columnsWithoutGroupColumn = columns.slice(showCodeAndDescription ? 2 : 1);
            const cols = ogqlColumnsToAgGrid(columnsWithoutGroupColumn);
            const data = mapQueryRowDataToColumnLabel(
              result.jobResult.data,
              columnsWithoutGroupColumn,
              showCodeAndDescription
            );
            const dataLabels = result.jobResult.data.map((row) =>
              showCodeAndDescription ? `${row[1]} - ${row[0]}` : row[0]
            );

            // 'child' is important! the array must have its parent at index 0 and something at index 1
            // if we were to support more depth or more parents than 1 then 'child'
            // would need to be unique to each set of relationships
            return ogqlDataToAgGrid(data, cols, dataLabels, false);
          }
          case EOgqlJobState.failed: {
            reportActionCreators.toggleMuiToast({
              type: MuiToastTypes.FAILURE,
              headerText: text.query.failure.headerText,
              bodyText: text.query.failure.bodyText,
              dataTest: `toast-dimensional-child-query-failure`
            });
            return result;
          }
          // we cancelled it .. it's not an error
          case EOgqlJobState.cancelled:
          default:
            return result;
        }
      });
    }
  )
}));
